
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Search, Download, Eye, DollarSign } from "lucide-react";
import { Gerador } from "@/types";
import { GeradorService } from "@/services/StorageService";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const GeradoresPage = () => {
  const [geradores, setGeradores] = useState<Gerador[]>([]);
  const [filteredGeradores, setFilteredGeradores] = useState<Gerador[]>([]);
  const [search, setSearch] = useState("");
  const { user, isAdmin, canAccessResource } = useAuth();

  useEffect(() => {
    // Carrega geradores com base na permissão do usuário
    const loadGeradores = () => {
      try {
        const geradoresData = isAdmin()
          ? GeradorService.getAll()
          : GeradorService.getByUserId(user?.id || "");
        
        setGeradores(geradoresData);
        setFilteredGeradores(geradoresData);
      } catch (error) {
        console.error("Erro ao carregar geradores:", error);
        toast.error("Erro ao carregar os geradores");
      }
    };
    
    loadGeradores();
  }, [user, isAdmin]);

  useEffect(() => {
    if (search) {
      const filtered = geradores.filter(gerador => 
        gerador.nomeTitular.toLowerCase().includes(search.toLowerCase()) ||
        gerador.documento.includes(search) ||
        gerador.cidade.toLowerCase().includes(search.toLowerCase()) ||
        gerador.estado.toLowerCase().includes(search.toLowerCase()) ||
        gerador.concessionaria.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredGeradores(filtered);
    } else {
      setFilteredGeradores(geradores);
    }
  }, [search, geradores]);

  const handleExport = () => {
    if (filteredGeradores.length === 0) return;
    
    const headers = [
      'Nome Titular', 'Documento', 'Cidade', 'Estado', 'Concessionária',
      'Potência (kWp)', 'Geração (kWh)', 'Tarifa (R$)', 'Investimento (R$)',
      'Payback (anos)', 'Créditos (kWh)'
    ];
    
    const rows = filteredGeradores.map(g => [
      g.nomeTitular,
      g.documento,
      g.cidade,
      g.estado,
      g.concessionaria,
      g.potenciaInstalada.toString(),
      g.geracaoEstimada.toString(),
      g.tarifaVenda.toString(),
      g.custoInvestimento.toString(),
      g.payback?.toFixed(2) || "N/A",
      g.creditosDisponiveis.toString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'geradores.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Verifica se o usuário pode acessar um gerador específico
  const userCanAccessGerador = (gerador: Gerador) => {
    return canAccessResource(gerador.criadoPor);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h1 className="text-3xl font-bold text-energy-blue-dark">Geradores</h1>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar geradores..."
                className="pl-8 w-full"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Link to="/geradores/novo">
              <Button className="w-full sm:w-auto bg-energy-green text-white hover:bg-energy-green-dark">
                <Plus className="mr-2 h-4 w-4" />
                Novo Gerador
              </Button>
            </Link>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Lista de Geradores</CardTitle>
                <CardDescription>
                  Total de {filteredGeradores.length} geradores {!isAdmin() && "seus "}cadastrados
                </CardDescription>
              </div>
              <Button
                variant="outline"
                className="text-energy-green-dark border-energy-green-dark hover:bg-energy-green/10"
                onClick={handleExport}
                disabled={filteredGeradores.length === 0}
              >
                <Download className="mr-2 h-4 w-4" />
                Exportar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">Nome do Titular</TableHead>
                    <TableHead>Localização</TableHead>
                    <TableHead>Concessionária</TableHead>
                    <TableHead className="text-right">Potência</TableHead>
                    <TableHead className="text-right">Geração</TableHead>
                    <TableHead className="text-right">Payback</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGeradores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center h-24 text-muted-foreground">
                        Nenhum gerador encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGeradores.map((gerador) => (
                      <TableRow key={gerador.id}>
                        <TableCell className="font-medium">
                          {gerador.nomeTitular}
                          <p className="text-xs text-muted-foreground truncate max-w-[220px]">
                            {gerador.documento}
                          </p>
                        </TableCell>
                        <TableCell>
                          {gerador.cidade}/{gerador.estado}
                        </TableCell>
                        <TableCell>{gerador.concessionaria}</TableCell>
                        <TableCell className="text-right">
                          {gerador.potenciaInstalada.toFixed(2)} kWp
                        </TableCell>
                        <TableCell className="text-right">
                          {gerador.geracaoEstimada.toFixed(2)} kWh/mês
                        </TableCell>
                        <TableCell className="text-right">
                          {gerador.payback ? (
                            <span className={gerador.payback < 6 ? "text-energy-green font-medium" : "font-medium"}>
                              {gerador.payback.toFixed(1)} anos
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end items-center space-x-1">
                            <Link to={`/geradores/${gerador.id}`}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                disabled={!userCanAccessGerador(gerador)}
                                title={!userCanAccessGerador(gerador) ? "Acesso restrito" : "Ver detalhes"}
                              >
                                <Eye className={`h-4 w-4 ${!userCanAccessGerador(gerador) ? "text-gray-300" : ""}`} />
                              </Button>
                            </Link>
                            <Link to={`/financeiro/gerador/${gerador.id}`}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0" 
                                title={!userCanAccessGerador(gerador) ? "Acesso restrito" : "Gestão Financeira"}
                                disabled={!userCanAccessGerador(gerador)}
                              >
                                <DollarSign className={`h-4 w-4 ${!userCanAccessGerador(gerador) ? "text-gray-300" : ""}`} />
                              </Button>
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default GeradoresPage;
